import { useMutation, useQuery, useLazyQuery } from '@apollo/client'
import { DataTableManual } from '@/components/shared/data-table-manual/data-table-manual'
import { columns } from './components/columns'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'
import { HeaderSection } from '@/components/shared/header-section'
import { PageContainer } from '@/components/shared/page-container'
import { CustomerRole } from '@/types/customer'
import { useState, useEffect } from 'react'
import { DataTableToolbar } from '@/components/shared/data-table-manual/data-table-toolbar-manual'
import { AccountManager } from './types'
import { ColumnDef } from '@tanstack/react-table'
import AccountManagerForm from './components/AccountManagerForm'
import { Sheet } from '@/components/ui/sheet'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import { GET_ACCOUNT_MANAGERS } from './accountManagerQueries'
import { DELETE_MANY_CUSTOMERS } from '../B2BCustomers/B2BCustomersQueries'
import { toast } from 'sonner'
import { useDeleteConfirmation } from '@/hooks/useDeleteConfirmation'
import { PageContent } from '@/components/shared/page-content'
import { getOrderSortParams } from '@/utils/orderUtils'

interface FilterOption {
  label: string
  value: string
}

export function AccountManagers() {
  const [searchTerm, setSearchTerm] = useState('')
  const [tableData, setTableData] = useState<AccountManager[]>([])
  const [tableDataCount, setTableDataCount] = useState(0)
  const [order, setOrder] = useState([])
  const [filters, setFilters] = useState<Map<string, Set<string>>>(new Map())

  const { onDelete } = useDeleteConfirmation({
    mutation: DELETE_MANY_CUSTOMERS,
    refetchQueries: ['GET_ACCOUNT_MANAGERS'],
    messages: {
      success: 'Account Managers deleted successfully',
      error: 'Failed to delete Account Managers',
    },
    itemName: 'Account Manager',
  })

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  })

  const [loadAccountManagers, { data, loading }] = useLazyQuery(
    GET_ACCOUNT_MANAGERS,
    {
      fetchPolicy: 'cache-and-network',
    }
  )

  // Prepare filters and graphQL params to query data
  useEffect(() => {
    const queryFilter = Array.from(filters.entries())
      .map(([key, value]) => {
        if (!value.size) return null

        // Handle role filter
        if (key === 'role') {
          return {
            role: {
              in: Array.from(value),
            },
          }
        }

        // Handle company name filter
        if (key === 'b2b_company_name') {
          return {
            b2b_company_name: {
              in: Array.from(value),
            },
          }
        }

        return {
          [key]: {
            in: Array.from(value),
          },
        }
      })
      .filter(Boolean)

    loadAccountManagers({
      variables: {
        roles: [
          CustomerRole.ACCOUNT_MANAGER,
          CustomerRole.AGENT,
          CustomerRole.ADMIN,
        ],
        searchTerm,
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
        orderBy: getOrderSortParams(order),
        filters: queryFilter,
      },
    })
  }, [searchTerm, pagination, order, filters])

  useEffect(() => {
    setPagination({ ...pagination, pageIndex: 0 })
  }, [searchTerm, pagination.pageSize, order])

  useEffect(() => {
    if (data) {
      setTableData(data.findManyCustomer)
      setTableDataCount(data.findManyCustomerCount)
    }
  }, [data])

  const accountManagers = tableData
  const totalCount = tableDataCount

  const uniqueCompanyNames = new Set<string>()
  accountManagers.forEach(manager => {
    if (manager.b2b_company_name) {
      uniqueCompanyNames.add(manager.b2b_company_name)
    }
  })

  const companyOptions: FilterOption[] = Array.from(uniqueCompanyNames).map(
    name => ({
      label: name,
      value: name,
    })
  )

  const roleOptions: FilterOption[] = [
    { label: 'Agent', value: CustomerRole.AGENT },
    { label: 'Manager', value: CustomerRole.ACCOUNT_MANAGER },
  ]

  const openAccountManagerForm = useOpenDrawer(AccountManagerForm)

  return (
    <PageContent>
      <div className="flex justify-between items-center">
        <HeaderSection
          title="Account Managers"
          subtitle="View and manage account managers"
        />
        <Button onClick={() => openAccountManagerForm({})}>
          <Plus className="mr-2 h-4 w-4" />
          Add Account Manager
        </Button>
      </div>

      <DataTableManual
        data={accountManagers}
        columns={columns as ColumnDef<AccountManager, unknown>[]}
        rowCount={totalCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        onSortingChange={setOrder}
        onColumnFiltersChange={setFilters}
        isLoading={loading}
        onRowClick={(row: AccountManager) => {
          openAccountManagerForm(row)
        }}
        toolbar={(table: any) => (
          <DataTableToolbar
            table={table}
            config={{
              onDelete: onDelete,
              search: {
                placeholder: 'Filter account managers...',
                accessor: 'name',
                onSearch: setSearchTerm,
                onFilter: setFilters,
              },
              columns: [
                // {
                //   title: 'Company',
                //   accessor: 'b2b_company_name',
                //   options: companyOptions,
                // },
                {
                  title: 'Role',
                  accessor: 'role',
                  options: roleOptions,
                },
              ],
            }}
          />
        )}
      />
    </PageContent>
  )
}
